import React from 'react';
import { FileParameter } from 'src/api/FiretrolApiClient';
import {
  useUploadDocument,
  useUploadDocumentToFolder,
} from 'src/api/queries/documents/useDocumentMutations';
import { ROOT_FOLDER_ID } from 'src/app/documents/documentConstants';
import { showSuccessToast } from 'src/utils';

export interface IDocumentUpload {
  uploadFiles: (files: File[]) => Promise<void>;
  showProgress: boolean;
  targetCount: number;
  completeCount: number;
  uploadPercent: number;
}

export interface IDocumentUploadOptions {
  jobId?: number;
  folderId: string;
}

export const useDocumentUpload = ({
  jobId,
  folderId,
}: IDocumentUploadOptions): IDocumentUpload => {
  const [showProgress, setShowProgress] = React.useState(false);
  const [targetCount, setTargetCount] = React.useState(0);
  const [completeCount, setCompleteCount] = React.useState(0);

  const { mutateAsync: mutateRoot } = useUploadDocument(jobId);
  const { mutateAsync: mutateFolder } = useUploadDocumentToFolder(jobId);

  const uploadFiles = React.useCallback(
    async (files: File[]) => {
      if (!jobId) return;
      try {
        setTargetCount(files.length);
        setShowProgress(true);
        await Promise.all(
          files.map(async (file) => {
            const fileParam: FileParameter = {
              data: file,
              fileName: file.name,
            };
            if (folderId === ROOT_FOLDER_ID) {
              await mutateRoot(fileParam);
            } else {
              await mutateFolder({ folderId, file: fileParam });
            }
            setCompleteCount((count) => count + 1);
          }),
        );
        showSuccessToast(
          `Upload Complete: ${files.length} files successfully uploaded!`,
        );
      } catch {
        // The mutation's error pipeline already logged and toasted the failure.
      } finally {
        setShowProgress(false);
        setTargetCount(0);
        setCompleteCount(0);
      }
    },
    [jobId, folderId, mutateRoot, mutateFolder],
  );

  return {
    uploadFiles,
    showProgress,
    targetCount,
    completeCount,
    uploadPercent: targetCount === 0 ? 0 : completeCount / targetCount,
  };
};
